import { Component } from '@theme/component';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

import { StandardEvents } from '@shopify/events';

import { isMobileBreakpoint }
  from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [liveRegion]
 */

class CartDrawerComponent
  extends Component {

  #summaryThreshold = 0.5;

  #historyAbortController = null;

  get drawer() {
    return this.closest('theme-drawer');
  }

  get dialog() {
    return this.drawer?.refs?.panel ?? this.closest('dialog');
  }

  showDialog() {
    this.drawer?.open();
  }

  closeDialog = async () => {
    await this.drawer?.close();
  };

  connectedCallback() {

    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAdd
    );

    this.drawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#updateStickyState
    );

    this.drawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.drawer?.addEventListener(
      DrawerCloseEvent.eventName,
      this.#handleHistoryClose
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    queueMicrotask(() => this.#renderRelatedProducts());
  }

  disconnectedCallback() {

    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAdd
    );

    this.drawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#updateStickyState
    );

    this.drawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.drawer?.removeEventListener(
      DrawerCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {

    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {

      history.pushState(
        { cartDrawerOpen: true },
        ''
      );
    }

    this.#historyAbortController =
      new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      {
        signal:
          this.#historyAbortController.signal
      }
    );
  };

  #handleHistoryClose = () => {

    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {

    if (this.dialog?.open) {

      this.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  #handleCartAdd = (event) => {

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(
      event.detail.resource?.item_count
    );

  };

  #announceCartCount(cartCount) {

    const liveRegion =
      this.refs.liveRegion;

    if (
      !this.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {

    this.showDialog();

    customElements.whenDefined(
      'shopify-payment-terms'
    ).then(() => {

      const installmentsContent =
        document.querySelector(
          'shopify-payment-terms'
        )?.shadowRoot;

      const cta =
        installmentsContent?.querySelector(
          '#shopify-installments-cta'
        );

      cta?.addEventListener(
        'click',
        this.closeDialog,
        { once: true }
      );

    });
  }

  close() {
    this.closeDialog();
  }

  #renderRelatedProducts = async () => {
    const dialog = this.dialog;
    if (!dialog) return;
    const summaryElement = dialog.querySelector('.cart-drawer__summary');
    if (!summaryElement) return;

    dialog.querySelector('.cart-drawer__related-products')?.remove();

    const relatedWrapper = document.createElement('section');
    relatedWrapper.className = 'cart-drawer__related-products';
    relatedWrapper.setAttribute('aria-labelledby', 'CartDrawerRecommendationsTitle');
    relatedWrapper.innerHTML = `
      <div class="cart-drawer__related-title">
        <h3 id="CartDrawerRecommendationsTitle">You may also like</h3>
        <div class="cart-drawer__related-actions">
          <span>${this.#escapeHtml(this.dataset.upsellDiscountPercent || '10')}% off</span>
          <button type="button" class="cart-drawer__related-arrow" data-upsell-slide="previous" aria-label="Previous products">‹</button>
          <button type="button" class="cart-drawer__related-arrow" data-upsell-slide="next" aria-label="Next products">›</button>
        </div>
      </div>
      <div class="cart-drawer__related-list" aria-busy="true">
        <div class="cart-drawer__related-skeleton"></div>
        <div class="cart-drawer__related-skeleton"></div>
        <div class="cart-drawer__related-skeleton"></div>
      </div>
      <p class="cart-drawer__related-status" role="status" aria-live="polite"></p>
    `;

    summaryElement.before(relatedWrapper);

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => {
        if (!response.ok) throw new Error('Could not load the cart.');
        return response.json();
      });

      if (!cart.items.length) {
        relatedWrapper.remove();
        return;
      }

      const collectionHandle = this.dataset.upsellCollectionHandle || 'cart-upsell-products';
      const collectionUrl = new URL(
        `${window.Shopify.routes.root}collections/${encodeURIComponent(collectionHandle)}/products.json`,
        location.origin
      );
      collectionUrl.searchParams.set('limit', '50');
      const collectionData = await fetch(collectionUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => {
        if (!response.ok) throw new Error('Could not load the Cart Upsell Products collection.');
        return response.json();
      });

      const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
      const products = (collectionData.products || [])
        .filter((product) => !cartProductIds.has(String(product.id)))
        .map((product) => ({
          product,
          variant: product.variants?.find((variant) => variant.available),
        }))
        .filter(({ variant }) => Boolean(variant));

      if (!products.length) {
        relatedWrapper.remove();
        return;
      }

      const list = relatedWrapper.querySelector('.cart-drawer__related-list');
      if (!list) return;
      list.setAttribute('aria-busy', 'false');
      list.innerHTML = products.map(({ product, variant }) => {
        const image = product.featured_image || product.images?.[0] || '';
        const imageUrl = typeof image === 'string' ? image : image.src || '';
        const priceCents = this.#priceToCents(variant.price ?? product.price);
        const discountPercent = Number(this.dataset.upsellDiscountPercent) || 10;
        const salePriceCents = Math.round(priceCents * (1 - discountPercent / 100));
        const productUrl = `${window.Shopify.routes.root}products/${encodeURIComponent(product.handle)}`;

        return `
          <article class="cart-drawer__related-product">
            <a href="${productUrl}" class="cart-drawer__related-image" tabindex="-1">
              <img src="${this.#escapeHtml(imageUrl)}" alt="" loading="lazy" width="120" height="120">
            </a>
            <div class="cart-drawer__related-info">
              <a href="${productUrl}" class="cart-drawer__related-name">${this.#escapeHtml(product.title)}</a>
              <div class="cart-drawer__related-price">
                <span class="cart-drawer__sale-price">${this.#formatMoney(salePriceCents, cart.currency)}</span>
                <s class="cart-drawer__compare-price">${this.#formatMoney(priceCents, cart.currency)}</s>
              </div>
              <button type="button" class="cart-drawer__related-add" data-variant-id="${variant.id}">Add</button>
            </div>
          </article>`;
      }).join('');
    } catch (error) {
      console.error('Related products error:', error);
      relatedWrapper.remove();
    }

    relatedWrapper.addEventListener('click', this.#handleUpsellAdd);
    relatedWrapper.addEventListener('click', this.#handleUpsellSlide);
  };

  #handleUpsellSlide = (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-upsell-slide]')
      : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const list = button.closest('.cart-drawer__related-products')?.querySelector('.cart-drawer__related-list');
    const card = list?.querySelector('.cart-drawer__related-product');
    if (!list || !card) return;

    const gap = Number.parseFloat(getComputedStyle(list).gap) || 8;
    const direction = button.dataset.upsellSlide === 'previous' ? -1 : 1;
    list.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap) * 2, behavior: 'smooth' });
  };

  #handleUpsellAdd = async (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('.cart-drawer__related-add')
      : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const wrapper = button.closest('.cart-drawer__related-products');
    const status = wrapper?.querySelector('.cart-drawer__related-status');
    const discountCode = this.dataset.upsellDiscountCode?.trim();
    const discountPercent = Number(this.dataset.upsellDiscountPercent) || 10;
    button.disabled = true;
    button.textContent = 'Adding…';
    if (status) status.textContent = '';

    try {
      const sections = ['cart-drawer-section', 'cart-icon-bubble'];
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: { '_Cart upsell': `${discountPercent}% off` },
          }],
          sections,
          sections_url: location.pathname,
        }),
      });
      const addedCart = await addResponse.json();
      if (!addResponse.ok) throw new Error(addedCart.description || addedCart.message || 'Could not add this item.');

      let updatedCart = addedCart;
      if (discountCode) {
        const currentCart = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((response) => response.json());
        const discountCodes = (currentCart.discount_codes || [])
          .filter((discount) => discount.applicable && discount.code)
          .map((discount) => discount.code);
        if (!discountCodes.some((code) => code.toLowerCase() === discountCode.toLowerCase())) {
          discountCodes.push(discountCode);
        }

        const discountResponse = await fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: discountCodes.join(','), sections, sections_url: location.pathname }),
        });
        updatedCart = await discountResponse.json();
        if (!discountResponse.ok) throw new Error('The product was added, but the discount could not be applied.');

        const applied = updatedCart.discount_codes?.some(
          (discount) => discount.code?.toLowerCase() === discountCode.toLowerCase() && discount.applicable
        );
        if (!applied) {
          console.warn(`Cart upsell discount code ${discountCode} is not configured or eligible.`);
        }
      }

      this.#replaceCartSections(
        updatedCart.sections || addedCart.sections,
        updatedCart.item_count
      );
    } catch (error) {
      console.error('Cart recommendation add error:', error);
      button.disabled = false;
      button.textContent = 'Try again';
      if (status) status.textContent = error.message || 'Could not add this product.';
    }
  };

  #replaceCartSections(sections, itemCount) {
    if (!sections) return;
    const parser = new DOMParser();
    let renderedCartCount = null;

    const drawerHtml = sections['cart-drawer-section'];
    if (drawerHtml) {
      const parsed = parser.parseFromString(drawerHtml, 'text/html');
      const nextDrawer = parsed.querySelector('#cart-drawer');
      const currentDrawer = document.querySelector('#cart-drawer');
      const nextDialog = nextDrawer?.querySelector('.theme-drawer__dialog');
      const currentDialog = currentDrawer?.querySelector('.theme-drawer__dialog');

      if (nextDialog && currentDialog) {
        const wasOpen = currentDialog.open || currentDrawer?.hasAttribute('open');
        renderedCartCount = nextDialog.querySelector('.theme-drawer__badge')?.textContent?.trim() || null;

        // Keep the live dialog itself so replacing its contents cannot close the drawer.
        currentDialog.innerHTML = nextDialog.innerHTML;

        if (wasOpen) {
          currentDrawer?.setAttribute('open', '');
          if (!currentDialog.open) {
            try {
              currentDialog.showModal();
            } catch (_error) {
              currentDialog.setAttribute('open', '');
            }
          }
        }
      }
    }

    const bubbleHtml = sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const parsed = parser.parseFromString(bubbleHtml, 'text/html');
      const nextSection = parsed.querySelector('#shopify-section-cart-icon-bubble, #cart-icon-bubble');
      const currentSection = document.querySelector(
        nextSection?.id === 'cart-icon-bubble' ? '#cart-icon-bubble' : '#shopify-section-cart-icon-bubble'
      );
      if (nextSection && currentSection) currentSection.replaceWith(nextSection);
    }

    const nextCartCount = renderedCartCount ?? itemCount;
    if (Number.isFinite(Number(nextCartCount))) {
      document.querySelectorAll('.truck-cart-count').forEach((count) => {
        count.textContent = String(nextCartCount);
      });
    }
  }

  #priceToCents(value) {
    const raw = String(value ?? '0');
    return raw.includes('.') ? Math.round(Number(raw) * 100) : Math.round(Number(raw));
  }

  #formatMoney(cents, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: currency || 'AUD',
    }).format(cents / 100);
  }

  #escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
    })[character]);
  }

  #updateStickyState = () => {

    const { dialog } =
      this.refs;

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {

      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  };
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {

  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
